/*
  50 Grams v2 (master design prompt).
  1. Sections fade and rise once as they enter the screen (aesthetic rule 7). Skipped with prefers-reduced-motion.
  2. Product page: gallery dots on phones, mirroring the shared gallery's thumbnails.
  3. Product page: sticky buy bar on phones once the main Preorder button has scrolled away.
  4. First tasters: the play button over a tester photo opens that tester's video in a modal.
*/
(() => {
  "use strict";
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // No reveal inside the Theme Editor (html.shopify-design-mode): a section the editor re-renders is a fresh
  // element the observer never saw, so it would stay hidden until a reload. Editing needs everything visible.
  const editing = document.documentElement.classList.contains("shopify-design-mode");
  const canReveal = !reduce && !editing && "IntersectionObserver" in window;
  if (canReveal) document.documentElement.classList.add("v2-reveal");

  function initReveal() {
    if (!canReveal) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        io.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -10% 0px" });
    document.querySelectorAll(".v2-main > .band").forEach((band) => io.observe(band));
    // A section loaded later (Theme Editor preview, or any section swapped in by script) joins the observer
    document.addEventListener("shopify:section:load", (e) => {
      const root = e.target instanceof Element ? e.target : document;
      root.querySelectorAll(".v2-main > .band:not(.is-in)").forEach((band) => io.observe(band));
    });
  }

  function initDots() {
    document.querySelectorAll("[data-v2-dots]").forEach((dots) => {
      const gallery = dots.closest("[data-gallery]");
      const thumbs = [...gallery.querySelectorAll(".gallery__thumb")];
      thumbs.forEach((thumb, n) => {
        const b = document.createElement("button");
        b.type = "button";
        b.setAttribute("aria-label", `Show image ${n + 1} of ${thumbs.length}`);
        b.addEventListener("click", () => thumb.click());
        dots.append(b);
      });
      const sync = () => thumbs.forEach((t, n) => {
        const v = t.getAttribute("aria-current") === "true" ? "true" : "false";
        if (dots.children[n].getAttribute("aria-current") !== v) dots.children[n].setAttribute("aria-current", v);
      });
      // Watch the thumbnails only: the dots live inside the gallery too, so watching the whole gallery would loop
      new MutationObserver(sync).observe(gallery.querySelector(".gallery__thumbs"), { subtree: true, attributes: true, attributeFilter: ["aria-current"] });
      sync();
    });
  }

  function initStickyBuy() {
    const bar = document.querySelector("[data-v2-sticky]");
    const form = bar && document.getElementById(bar.querySelector("[data-for]").dataset.for);
    if (!bar || !form) return;
    const atc = form.querySelector("[data-atc]");
    const footer = document.querySelector(".site-footer");
    const name = bar.querySelector("[data-v2-sticky-name]");
    let ticking = false;
    // Shown once the main Preorder button is above the screen, hidden again over the footer.
    // A position check on scroll, because on phones the button starts below the first screen.
    const update = () => {
      ticking = false;
      const atcAbove = atc.getBoundingClientRect().bottom < 0;
      const footerIn = footer ? footer.getBoundingClientRect().top < innerHeight : false;
      bar.hidden = !(atcAbove && !footerIn);
    };
    addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    addEventListener("resize", update);
    update();
    form.addEventListener("flavor-change", (e) => { name.textContent = e.detail.name; });
    if (form.flavorName && form.flavorName()) name.textContent = form.flavorName();
  }

  // Contact form (prototype only; Shopify's contact form takes over in the build):
  // inline errors under each field, focus moves to the first problem, success replaces the form
  function initContactForm() {
    document.querySelectorAll("[data-contact-form]").forEach((form) => {
      const rules = [
        ["#c-name", (v) => v.trim() !== ""],
        ["#c-email", (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())],
        ["#c-message", (v) => v.trim() !== ""],
      ];
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        let first = null;
        rules.forEach(([sel, ok]) => {
          const input = form.querySelector(sel);
          const good = ok(input.value);
          input.setAttribute("aria-invalid", String(!good));
          form.querySelector(sel + "-err").hidden = good;
          if (!good && !first) first = input;
        });
        if (first) { first.focus(); return; }
        form.querySelector("[data-contact-fields]").hidden = true;
        const done = form.querySelector("[data-contact-done]");
        done.hidden = false;
        done.focus();
      });
    });
  }

  // Product page: the price line follows the pack picked (the "Both" card is two 4 packs, one of each flavor)
  function initPackUnit() {
    document.querySelectorAll("[data-flavor-form]").forEach((form) => {
      const unit = form.querySelector("[data-price-unit]");
      if (!unit) return;
      const single = unit.textContent;
      const sync = () => {
        const card = form.querySelector("[role='radio'][aria-checked='true']");
        unit.textContent = (card && card.dataset.unit) || single;
      };
      form.addEventListener("flavor-change", sync);
      sync();
    });
  }

  // Tabs (What is in it): click, or arrow keys / Home / End with automatic activation;
  // one tab stop for the whole tab list (roving tabindex)
  function initTabs() {
    document.querySelectorAll("[data-v2-tabs]").forEach((box) => {
      const tabs = [...box.querySelectorAll("[role='tab']")];
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute("aria-selected", String(on));
          t.tabIndex = on ? 0 : -1;
          document.getElementById(t.getAttribute("aria-controls")).hidden = !on;
          box.querySelectorAll("[data-for-panel='" + t.getAttribute("aria-controls") + "']").forEach((m) => (m.hidden = !on));
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, n) => {
        tab.addEventListener("click", () => select(tab, false));
        tab.addEventListener("keydown", (e) => {
          const next = { ArrowRight: n + 1, ArrowLeft: n - 1, Home: 0, End: tabs.length - 1 }[e.key];
          if (next === undefined) return;
          e.preventDefault();
          select(tabs[(next + tabs.length) % tabs.length], true);
        });
      });
    });
  }

  // What goes in the cup (product pages): the spoon turns as the section passes through the screen,
  // and the ingredient under the spoon is named below the bowl. Reduced motion: the spoon stays still.
  // The section (sections/ri-ingredient-bowl.liquid) can set the motion with data attributes:
  // data-bowl-start (deg), data-bowl-turn (deg), data-bowl-from and data-bowl-to (0 to 1 of the screen height),
  // data-bowl-animate="false". Without them the prototype values apply.
  // One scroll listener serves every bowl; a section the Theme Editor adds or reloads starts on its own.
  function initBowl() {
    const bowls = new Set();
    const num = (value, fallback) => {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : fallback;
    };
    const setup = (sec) => {
      const spin = sec.querySelector(".v2-bowl__spin");
      const stage = sec.querySelector(".v2-bowl__stage");
      if (!spin || !stage || sec.dataset.bowlReady === "true") return;
      sec.dataset.bowlReady = "true";
      const names = [...sec.querySelectorAll(".v2-bowl__ring text")];
      const start = num(sec.dataset.bowlStart, -20);
      const turn = num(sec.dataset.bowlTurn, 252);
      const from = num(sec.dataset.bowlFrom, 0.7);
      const to = num(sec.dataset.bowlTo, 0.1);
      const light = (deg) => {
        const d = ((deg % 360) + 360) % 360;
        let best = null, gap = 999;
        names.forEach((t) => { const a = +t.dataset.angle; const g = Math.min(Math.abs(a - d), 360 - Math.abs(a - d)); if (g < gap) { gap = g; best = t; } });
        names.forEach((t) => t.classList.toggle("is-on", t === best));
      };
      const turnTo = (deg) => {
        spin.style.setProperty("--rot", deg.toFixed(2) + "deg");
        light(deg);
      };
      // Reduced motion keeps the spoon still, unless the section setting turns it anyway (it only follows the scroll)
      const still = (reduce && sec.dataset.bowlIgnoreReduce !== "true") || sec.dataset.bowlAnimate === "false";
      if (still) { turnTo(start); return; }
      // the spoon waits at the start angle (prototype: top, slightly left, -20deg) until the bowl is well on screen,
      // then turns clockwise (prototype: 252deg, 70% of a circle) while the bowl centre moves from `from` to `to`
      // of the screen height (prototype: 70% to 10%)
      const bowl = {
        sec,
        update() {
          const st = stage.getBoundingClientRect();
          const c = st.top + st.height / 2;
          const span = Math.max(0.01, from - to);
          const p = Math.min(1, Math.max(0, (innerHeight * from - c) / (innerHeight * span)));
          turnTo(start + p * turn);
        },
      };
      bowls.add(bowl);
      bowl.update();
    };
    let ticking = false;
    const updateAll = () => {
      ticking = false;
      bowls.forEach((bowl) => {
        if (!bowl.sec.isConnected) bowls.delete(bowl);
        else bowl.update();
      });
    };
    // Capture, so the scroll of any element counts: from 990px Horizon scrolls .page-wrapper, not the window
    document.addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(updateAll); } }, { capture: true, passive: true });
    addEventListener("resize", updateAll);
    document.querySelectorAll("[data-bowl]").forEach(setup);
    document.addEventListener("shopify:section:load", (e) => {
      const root = e.target instanceof Element ? e.target : document;
      root.querySelectorAll("[data-bowl]").forEach(setup);
    });
  }

  // First tasters: the play button over a tester photo opens that tester's video in a native dialog
  // (markup in sections/ri-first-tasters-configurable.liquid, styles .v2-video-modal in 50g-v2.css).
  // One delegated click listener on the document, so a section the Theme Editor reloads works without
  // rebinding and no listener is ever added twice. Nothing loads before the button is pressed: the player
  // (video or YouTube/Vimeo iframe) waits in an inert template and is copied into the frame on open.
  // Closing removes the player again, which stops playback, frees the page scroll
  // and returns focus to the button that opened the dialog.
  function initVideoModals() {
    const LOCK = "v2-modal-open";
    const openers = new WeakMap();
    const syncLock = () => {
      document.documentElement.classList.toggle(LOCK, !!document.querySelector("dialog[data-v2-video-modal][open]"));
    };
    const stop = (dialog) => {
      const video = dialog.querySelector("video");
      if (video) video.pause();
      dialog.querySelectorAll("[data-v2-video-frame] > :not(template)").forEach((node) => node.remove());
    };
    const mount = (dialog) => {
      const frame = dialog.querySelector("[data-v2-video-frame]");
      const template = frame && frame.querySelector("template[data-v2-video-template]");
      if (!frame || !template) return null;
      if (!frame.querySelector("video, iframe")) frame.append(template.content.cloneNode(true));
      return frame.querySelector("video");
    };
    const afterClose = (dialog) => {
      stop(dialog);
      syncLock();
      const opener = openers.get(dialog);
      openers.delete(dialog);
      if (opener && document.contains(opener)) opener.focus();
    };
    const close = (dialog) => {
      if (typeof dialog.close === "function") {
        if (dialog.open) dialog.close(); // fires the close event, which runs afterClose
      } else {
        dialog.removeAttribute("open");
        afterClose(dialog);
      }
    };
    const prepare = (dialog) => {
      if (dialog.dataset.v2Ready === "true") return;
      dialog.dataset.v2Ready = "true";
      // Escape runs through the native cancel and close events
      dialog.addEventListener("close", () => afterClose(dialog));
      // The dialog fills the screen, so a click that lands on the dialog itself is a click on the backdrop
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog || e.target.closest("[data-v2-video-close]")) close(dialog);
      });
      // Browsers without showModal(): Escape by hand
      if (typeof dialog.showModal !== "function") {
        dialog.addEventListener("keydown", (e) => { if (e.key === "Escape") close(dialog); });
      }
    };
    const open = (button) => {
      const dialog = document.getElementById(button.getAttribute("aria-controls") || "");
      if (!dialog || !dialog.hasAttribute("data-v2-video-modal") || dialog.open) return;
      prepare(dialog);
      openers.set(dialog, button);
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
      syncLock();
      const video = mount(dialog);
      if (video) {
        const started = video.play();
        if (started && typeof started.catch === "function") started.catch(() => {});
      }
      const closeButton = dialog.querySelector("[data-v2-video-close]");
      if (closeButton) closeButton.focus();
    };
    document.addEventListener("click", (e) => {
      const button = e.target instanceof Element ? e.target.closest("[data-v2-video-open]") : null;
      if (button) open(button);
    });
    // Theme Editor: a reloaded section takes its open dialog with it, so the scroll lock is checked again
    document.addEventListener("shopify:section:load", syncLock);
    document.addEventListener("shopify:section:unload", syncLock);
  }

  // From 990px Horizon scrolls .page-wrapper and keeps html locked to the screen. If html still gets scrolled
  // (a third party element taller than the screen, or the Theme Editor scrolling the window to keep your place
  // or show a section), the whole page lifts and a blank band shows below it. Hand that scroll back to
  // .page-wrapper so the same content stays in view and html returns to the top. 50g-base.css has the CSS half.
  function initScrollLock() {
    const wrapper = document.querySelector(".page-wrapper");
    const root = document.scrollingElement;
    const squeeze = matchMedia("(min-width: 990px)");
    if (!wrapper || !root) return;
    const settle = () => {
      if (!squeeze.matches) return;
      const y = root.scrollTop;
      if (!y) return;
      root.scrollTop = 0;
      wrapper.scrollTo({ top: wrapper.scrollTop + y, behavior: "instant" });
    };
    addEventListener("scroll", settle, { passive: true });
    document.addEventListener("shopify:section:load", () => requestAnimationFrame(settle));
    settle();
  }

  // Theme setting "Product pages open on the first variant": snippets/g50-first-variant.liquid sends the page
  // to ?variant=<first> before it draws. This is the backstop (for example in the Theme Editor): when the page
  // opens without ?variant=,
  // pick the first variant's values so Horizon redraws price, media, buttons and blocks for it,
  // the same way as when a shopper clicks it. Shopify itself opens on the first variant in stock.
  function initFirstVariant() {
    if (new URL(window.location.href).searchParams.has("variant")) return;
    const picker = document.querySelector("variant-picker[data-g50-first-options][data-template-product-match='true']");
    if (!picker) return;
    let wanted;
    try {
      wanted = JSON.parse(picker.dataset.g50FirstOptions);
    } catch (error) {
      return;
    }
    if (!Array.isArray(wanted)) return;

    customElements.whenDefined("variant-picker").then(() => {
      const groups = picker.querySelectorAll(".variant-picker__form fieldset, .variant-picker__form select");
      let changed = null;
      groups.forEach((group, index) => {
        const value = wanted[index];
        if (value == null) return;
        if (group instanceof HTMLSelectElement) {
          const option = [...group.options].find((item) => item.value === value || item.textContent.trim() === value);
          if (option && !option.selected) {
            group.value = option.value;
            changed = group;
          }
          return;
        }
        const radio = [...group.querySelectorAll('input[type="radio"]')].find((item) => item.value === value);
        if (radio && !radio.checked) {
          radio.checked = true;
          changed = radio;
        }
      });
      if (changed) changed.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  // Text blocks with [variant] (snippets/text.liquid): show the newly selected variant's name after a change
  function initVariantNames() {
    document.addEventListener(
      "shopify:product:select",
      (event) => {
        const section = event.target instanceof Element ? event.target.closest(".shopify-section") : null;
        if (!section || event.target.closest("product-card") || !event.promise) return;
        const slots = section.querySelectorAll("[data-g50-variant-name]");
        if (!slots.length) return;
        event.promise
          .then(({ detail }) => {
            if (!detail || !detail.html) return;
            slots.forEach((slot) => {
              const id = slot.getAttribute("data-g50-variant-name");
              const next = detail.html.querySelector(`[data-g50-variant-name="${CSS.escape(id)}"]`);
              if (next) slot.textContent = next.textContent;
            });
          })
          .catch(() => {});
      },
      true
    );
  }

  // Product details: a block with no height (for example an app block with nothing to show) gets .g50-gapless,
  // so it does not add a second gap. Watched with ResizeObserver, so it updates when the block fills or empties.
  function initGapless() {
    if (!("ResizeObserver" in window)) return;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        el.classList.toggle("g50-gapless", el.getBoundingClientRect().height === 0);
      });
    });
    const watch = (root) => {
      root.querySelectorAll(".product-details .group-block-content > *").forEach((el) => {
        const display = getComputedStyle(el).display;
        if (display === "none" || display === "contents" || el.dataset.g50Gapless) return;
        el.dataset.g50Gapless = "watched";
        observer.observe(el);
      });
    };
    watch(document);
    document.addEventListener("shopify:section:load", (event) => watch(event.target instanceof Element ? event.target : document));
  }

  // Cart preorder terms: "Orders close at 3:30 PM ET each day." from the Order Delivery Date (Identixweb) app.
  // The app has no public way to read its cutoff, so this reads it from its date picker on the page. The line
  // stays hidden when the picker has no cutoff, the cutoff is off, the picker holds two different cutoffs, or
  // an app update changes these names. The body observer covers the picker loading and cart updates.
  function readOrderCutoff() {
    const picker = document.querySelector(".identixweb-order-delivery");
    if (!picker) return null;

    const cutoffs = new Map();
    let timeZone = "";
    const seen = new WeakSet();
    const walk = (value, depth) => {
      if (!value || typeof value !== "object" || depth > 5 || value instanceof Node || seen.has(value)) return;
      seen.add(value);
      Object.keys(value).slice(0, 120).forEach((key) => {
        if (/token|auth|secure|^_|^(return|child|sibling|alternate|stateNode|owner)$/i.test(key)) return;
        const item = value[key];
        if (key === "time_zone" && typeof item === "string") timeZone = item;
        if (key === "cutoff_time" && item && "cutoff_12" in item) cutoffs.set(`${item.cutoff_time_status}|${item.cutoff_12}`, item);
        walk(item, depth + 1);
      });
    };

    [picker, ...picker.querySelectorAll("*")].slice(0, 150).forEach((el) => {
      const fiberKey = Object.keys(el).find((key) => key.startsWith("__reactFiber"));
      let fiber = fiberKey && el[fiberKey];
      for (let up = 0; fiber && up < 25; up += 1) {
        walk(fiber.memoizedProps, 0);
        let hook = fiber.memoizedState;
        for (let next = 0; hook && next < 25; next += 1) {
          walk(hook.memoizedState, 0);
          hook = hook.next;
        }
        fiber = fiber.return;
      }
    });

    if (cutoffs.size !== 1 || !timeZone) return null;
    const cutoff = [...cutoffs.values()][0];
    const time = String(cutoff.cutoff_12 || "").trim().replace(/^0/, "");
    if (Number(cutoff.cutoff_time_status) !== 1 || !/^\d{1,2}:\d{2} [AP]M$/.test(time)) return null;

    const zone = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortGeneric" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    return zone ? `${time} ${zone}` : null;
  }

  function initOrderCutoff() {
    if (!document.querySelector("[data-g50-order-cutoff]")) return;
    const update = () => {
      let text = null;
      try {
        text = readOrderCutoff();
      } catch (error) {
        text = null;
      }
      document.querySelectorAll("[data-g50-order-cutoff]").forEach((line) => {
        const slot = line.querySelector("[data-g50-order-cutoff-time]");
        if (text && slot && slot.textContent !== text) slot.textContent = text;
        if (line.hidden !== !text) line.hidden = !text;
      });
    };
    let timer;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(update, 300);
    };
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    window.addEventListener("pageshow", schedule);
    window.addEventListener("focus", schedule);
    update();
  }

  // Ship dates: PreProduct's current date wins. Liquid can only read the selling plan, which PreProduct does not
  // always update, so the printed dates (window.g50ShipDate.shown, from snippets/50g-assets) are swapped here.
  // With no PreProduct date, a bad format, a past date or a failed request, the printed date stays. Policy text
  // (.rte) is skipped, so a "last updated" date that matches is never changed.
  function initShipDate() {
    const config = window.g50ShipDate;
    if (!config || !config.product || !config.shown || !config.shown.length) return;
    fetch(`https://api.preproduct.io/api/preproducts/${config.product}.json?shop=${encodeURIComponent(config.shop)}`)
      .then((response) => (response.status === 200 ? response.json() : null))
      .then((data) => {
        const live = String((data && data.different_lead_time) || "").trim();
        if (!/^[A-Z][a-z]+ \d{1,2}, \d{4}$/.test(live)) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (new Date(live) < today) return;
        const old = config.shown.map((date) => date.trim()).filter((date) => date && date !== live);
        if (!old.length) return;

        const swap = () => {
          const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
          const nodes = [];
          while (walker.nextNode()) {
            const node = walker.currentNode;
            if (old.some((date) => node.nodeValue.includes(date))) nodes.push(node);
          }
          nodes.forEach((node) => {
            if (node.parentElement.closest("script, style, .rte, rte-formatter")) return;
            old.forEach((date) => {
              if (node.nodeValue.includes(date)) node.nodeValue = node.nodeValue.split(date).join(live);
            });
          });
        };
        swap();
        let timer;
        new MutationObserver(() => {
          clearTimeout(timer);
          timer = setTimeout(swap, 200);
        }).observe(document.body, { childList: true, subtree: true });
      })
      .catch(() => {});
  }

  document.addEventListener("DOMContentLoaded", () => {
    initShipDate();
    initOrderCutoff();
    initGapless();
    initVariantNames();
    initFirstVariant();
    initScrollLock();
    initReveal();
    initVideoModals();
    initBowl();
    initTabs();
    initPackUnit();
    // After components.js has wired the gallery and flavor form (both run on DOMContentLoaded, components.js first)
    initDots();
    initStickyBuy();
    initContactForm();
  });
})();
